import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { providerCustomersService, type ProviderCustomerCreateResult } from '@/services/provider/provider-customers.service';
import {
  ProviderCustomerCreateInput,
  ProviderCustomerDeleteInput,
  ProviderCustomerListParams,
  ProviderCustomerUpdateInput,
} from '@/types/provider-customers';

export const providerCustomerKeys = {
  all: ['provider', 'customers'] as const,
  list: (params: ProviderCustomerListParams | undefined) => [
    ...providerCustomerKeys.all,
    'active',
    params?.search ?? '',
    params?.teamRole ?? '',
    params?.status ?? '',
    params?.teamId ?? '',
    params?.sort ?? '',
    params?.page ?? 1,
    params?.perPage ?? 10,
  ] as const,
  deletedList: (params: ProviderCustomerListParams | undefined) => [
    ...providerCustomerKeys.all,
    'deleted',
    params?.search ?? '',
    params?.teamRole ?? '',
    params?.status ?? '',
    params?.teamId ?? '',
    params?.sort ?? '',
    params?.page ?? 1,
    params?.perPage ?? 10,
  ] as const,
};

export function useProviderCustomers(params?: ProviderCustomerListParams) {
  return useQuery({
    queryKey: providerCustomerKeys.list(params),
    queryFn: () => providerCustomersService.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useProviderDeletedCustomers(params?: ProviderCustomerListParams) {
  return useQuery({
    queryKey: providerCustomerKeys.deletedList(params),
    queryFn: () => providerCustomersService.listDeleted(params),
    placeholderData: keepPreviousData,
  });
}

export function useProviderCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: [...providerCustomerKeys.all, customerId ?? ''],
    queryFn: () => providerCustomersService.get(customerId as string),
    enabled: Boolean(customerId),
  });
}

export function useCreateProviderCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProviderCustomerCreateInput): Promise<ProviderCustomerCreateResult> =>
      providerCustomersService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerCustomerKeys.all });
    },
  });
}

export function useUpdateProviderCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProviderCustomerUpdateInput) => providerCustomersService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerCustomerKeys.all });
    },
  });
}

export function useDeleteProviderCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProviderCustomerDeleteInput) => providerCustomersService.delete(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerCustomerKeys.all });
    },
  });
}

export function useRestoreProviderCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) => providerCustomersService.restore(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerCustomerKeys.all });
    },
  });
}
